import { SmsSettingsForm, SmsSpendSummary, SmsTestPanel } from '@today/ui-domain'
import { type SmsSettingsSaveError, useSmsSettings } from '@/hooks/useSmsSettings'
import type { TenantConfigSectionProps } from '../section-props'

/** The message a `details.field` refusal puts next to its field (TO-383 Business rules). */
const FIELD_MESSAGES: Readonly<Record<string, string>> = {
	mode: 'Choose Managed or your own Twilio account.',
	fromIdentity: 'Enter a Messaging Service SID (MG followed by 32 hex characters); required with your own account.',
	credentials: 'Fill in all three credentials, or leave them all empty to keep the stored ones.',
	'credentials.accountSid': 'Not a Twilio Account SID (AC followed by 32 hex characters).',
	'credentials.apiKeySid': 'Not a Twilio API Key SID (SK followed by 32 hex characters).',
	'credentials.apiKeySecret': 'Not a valid API Key Secret.',
	allowedCountries: 'Use two-letter ISO country codes, each once.',
	monthlySpendCapEur: 'Enter a whole number of euros, or leave it empty for no cap.',
	smsNotificationTypes: 'Pick at least one notification type, or allow all types.',
}

const fieldErrorsOf = (error: SmsSettingsSaveError | null): Record<string, string> =>
	error?.kind === 'invalid' ? { [error.field]: FIELD_MESSAGES[error.field] ?? 'Not valid.' } : {}

const formErrorOf = (error: SmsSettingsSaveError | null): string | null => {
	if (error?.kind === 'invalid') return 'Some fields need attention.'
	if (error?.kind === 'unavailable') return 'The credential store is unavailable right now. Try again later.'
	if (error?.kind === 'failed') return 'The settings could not be saved. Try again.'
	return null
}

/**
 * The console's `sms` section (TO-383 REQ-014): the tenant's SMS settings,
 * this month's estimated spend and the test SMS. Both planes; the route tree
 * follows the plane, and READ access shows the settings without a save or a test.
 */
export function SmsSection({ tenantId, tenantSlug, plane, readOnly }: TenantConfigSectionProps) {
	const sms = useSmsSettings(tenantId, tenantSlug, plane)

	if (sms.loadFailed) {
		return (
			<div role="alert" className="flex flex-col items-start gap-3 text-sm text-ds-danger">
				<p>Could not load the SMS settings.</p>
				<button type="button" onClick={sms.reload} className="rounded-md border px-3 py-1">
					Try again
				</button>
			</div>
		)
	}
	if (!sms.settings) {
		return (
			<p className="text-sm text-ds-text-muted" aria-live="polite">
				Loading SMS settings…
			</p>
		)
	}

	const { settings } = sms
	return (
		<div className="flex flex-col gap-8">
			<SmsSpendSummary spend={settings.spend} monthlySpendCapEur={settings.monthlySpendCapEur} />
			<SmsSettingsForm
				key={settings.updatedAt ?? 'unconfigured'}
				settings={settings}
				readOnly={readOnly}
				isBusy={sms.saving}
				fieldErrors={fieldErrorsOf(sms.saveError)}
				error={formErrorOf(sms.saveError)}
				onSave={sms.save}
			/>
			<SmsTestPanel
				lastTest={settings.lastTest}
				readOnly={readOnly}
				isBusy={sms.testing}
				sent={sms.testSent}
				error={sms.testError}
				onSend={sms.sendTest}
			/>
		</div>
	)
}
